<template>
    <div
        v-if="changed"
        role="status"
        data-testid="project-change-notice"
        class="flex flex-col gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 sm:flex-row sm:items-center sm:justify-between"
    >
        <p class="text-sm text-taupe-700">
            Data changed. Refresh to see the latest.
        </p>
        <zen-button
            variant="secondary"
            size="field"
            data-testid="project-change-notice-refresh"
            @click="refresh"
        >
            Refresh
        </zen-button>
    </div>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue'

import { listenForProjectChanges } from '@/composables/useProjectChanges'

/**
 * Tells the user that this screen's data changed elsewhere, through the
 * Management API or MCP, and offers a refresh. Aggregate views do not refetch
 * on every event, so the change waits for the user. Events named in
 * `autoRefreshOn` refetch straight away instead.
 */
const props = defineProps({
    projectKey: {
        type: String,
        default: undefined,
    },
    autoRefreshOn: {
        type: Array,
        default: () => [],
    },
})

const emit = defineEmits(['refresh'])

const changed = ref(false)

const stopListening = listenForProjectChanges({
    getProjectKey: () => props.projectKey,
    onChange: (eventName) => {
        if (props.autoRefreshOn.includes(eventName)) {
            emit('refresh')
            return
        }

        changed.value = true
    },
    onResync: () => {
        changed.value = true
    },
})

function refresh() {
    changed.value = false
    emit('refresh')
}

onBeforeUnmount(stopListening)
</script>
